import type { FileExtension } from '../../../shared/domain/files/fileExtensions.no-deps'
import { htmlCam, htmlFilesSelect } from '../upload/htmlFilesSelect'
import type { UploadQueue } from './UploadQueue'
import type { FileId } from '../../../shared/domain/files/types.no-deps'
import { createFileUploadData } from './FileUploadData'
import { runAsyncTasksInOrder } from '../util/Promise'
import type { UploadGroupId } from './Upload'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'

export interface HtmlSelectSaveAndUpload {
    acceptedExtensions: FileExtension[]
    generateFileId: () => FileId
    groupId?: UploadGroupId
    sizeLimitInBytes?: number
    uploadQueue: UploadQueue
    writeFileContentToDisk: (fileId: FileId, file: File) => Promise<void>
    customer: number
    logger: ClientLogger
    isIOS?: boolean
    type?: 'image' | 'video'
}

export async function htmlSelectSaveAndUpload(args: HtmlSelectSaveAndUpload) {
    const {
        acceptedExtensions,
        generateFileId,
        groupId,
        sizeLimitInBytes,
        uploadQueue,
        writeFileContentToDisk,
        customer,
        logger,
        isIOS,
        type,
    } = args

    const files = isIOS ? await htmlCam(type, false) : await htmlFilesSelect(acceptedExtensions, true)

    const saveToDisks: Array<() => Promise<void>> = []
    const output = []

    for (const file of files) {
        const fileId = generateFileId()
        const fileUploadData = createFileUploadData(fileId, file.name, file.size, file.type)
        await uploadQueue.addUpload(fileUploadData, acceptedExtensions, sizeLimitInBytes, customer, groupId)

        const saveToDisk = async () => {
            try {
                logger.log('Starting writeFileContentToDisk.', { fileId })
                await writeFileContentToDisk(fileId, file)
                logger.log('Starting writeToDiskComplete.', { fileId })
                await uploadQueue.writeToDiskComplete(fileId, logger)
                logger.log('Updated queue that file was written to disk.', { fileId })
            } catch (e) {
                logger.error(e, { fileId })
                await uploadQueue.failedToWriteToDisk(fileId, logger).catch((err) => logger.error(err, { fileId }))
            }
        }

        output.push(fileUploadData)
        saveToDisks.push(saveToDisk)
    }

    // Do not await on this it will run in the background and update the upload status accordingly.
    // They are ran one by one to avoid reading and writing too much to disk.
    runAsyncTasksInOrder(saveToDisks)

    return output
}
